// Master Apps · colunas da tabela do Inventário — ordenação, texto exibido e
// filtro por coluna. TypeScript puro (sem React/DOM), separado da tela para
// rodar no `bun test` do monorepo (ver apps/api/tests/web-inventory-columns.test.ts),
// no mesmo arranjo de `deck.ts` e `compliance.ts`. Imports RELATIVOS de
// propósito: o runner do api não resolve o alias `@/*` do web em runtime.

import { diasUteisDoMesDe } from '@masterapps/shared';
import type { App } from './catalog-config';
import { STATUS_TYPES } from './catalog-config';
import { DOC_KEYS, docsFilled } from './compliance';
import { formatDateBR, formatUsdBR } from './format';

/** Colunas ordenáveis e filtráveis da tabela — Ações fica de fora de propósito. */
export type ColunaTabela =
  | 'aplicacao'
  | 'visibilidade'
  | 'situacao'
  | 'responsavel'
  | 'apresentacao'
  | 'docs'
  | 'dias'
  | 'atualizada'
  | 'custo';

/**
 * Custo desconhecido na ORDENAÇÃO. A coluna guarda dólares (sempre ≥ 0), então
 * qualquer negativo agrupa o "—" num extremo só: primeiro na crescente, último
 * na decrescente (que é a leitura útil, "quem gasta mais"). Não pode ser 0 —
 * empataria "não sei" com "custo zero", que são coisas diferentes.
 */
export const CUSTO_DESCONHECIDO = -1;

/** Ordenação ativa: coluna + direção (1 asc, -1 desc). Null = ordem de cadastro. */
export type OrdemTabela = { col: ColunaTabela; dir: 1 | -1 } | null;

/**
 * Valor COMPARÁVEL da coluna — ordenar usa este; para tipo/situação é o RÓTULO
 * exibido (ordenar por 'fullstack'/'web' cru embaralharia o que o olho vê).
 *
 * `custoUsd` entra por parâmetro porque o custo NÃO é campo da aplicação: vem de
 * `GET /api/railway/costs`, casado pelo repositório. Ordenar por ele usa o
 * NÚMERO (não o texto "US$ 1,88", que colocaria 10,00 antes de 9,00).
 */
export function valorColuna(
  a: App,
  col: ColunaTabela,
  custoUsd: number | null = null,
): string | number | null {
  switch (col) {
    case 'custo':
      return custoUsd ?? CUSTO_DESCONHECIDO;
    case 'aplicacao':
      return a.name;
    case 'visibilidade':
      return a.visibility;
    case 'situacao':
      return STATUS_TYPES[a.status]?.label ?? a.status;
    case 'responsavel':
      return a.owner;
    case 'apresentacao':
      // Visíveis primeiro na crescente (0 < 1).
      return a.apresentacaoDesabilitada ? 1 : 0;
    case 'docs':
      return docsFilled(a);
    case 'dias':
      // `null` = sem coleta; o comparador manda null para o FIM nas duas direções.
      return diasComAcesso(a);
    case 'atualizada':
      // ISO YYYY-MM-DD ordena certo como texto.
      return a.lastUpdate ?? '';
  }
}

/**
 * Compara dois valores de coluna para a ordenação. Número compara como número,
 * texto por localeCompare pt-BR. `null` ("não sei" — hoje só Dias úteis) vai
 * SEMPRE por último: a direção da seta reordena o que existe, não o vazio; por
 * isso a decisão sobre null é tomada aqui, ANTES de multiplicar pela direção.
 */
export function compararColuna(
  va: string | number | null,
  vb: string | number | null,
  dir: 1 | -1,
): number {
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const cmp =
    typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' });
  return cmp * dir;
}

/** Rótulos da coluna Apresentação — texto exibido na célula e no filtro. */
export const APRESENTACAO_ROTULO = { visivel: 'Visível', oculto: 'Oculto' } as const;

/** Opções FIXAS do filtro de Apresentação (o `value` é o que fica no estado). */
export const APRESENTACAO_OPCOES = [
  { value: '', label: 'Todos' },
  { value: 'visivel', label: 'Visíveis' },
  { value: 'oculto', label: 'Ocultos' },
];

/** Faixas do filtro Dias úteis. `sem` = nunca coletado (null). */
export const DIAS_OPCOES = [
  { value: '', label: 'Todos' },
  { value: 'sem', label: 'Sem coleta' },
  { value: '0-5', label: '0–5 dias' },
  { value: '6-10', label: '6–10 dias' },
  { value: '11+', label: '11+ dias' },
];

/** Em que faixa do filtro cai a contagem de dias (null = 'sem'). */
export function faixaDias(dias: number | null): string {
  if (dias === null) return 'sem';
  if (dias <= 5) return '0-5';
  if (dias <= 10) return '6-10';
  return '11+';
}

/**
 * Dias úteis com acesso desta aplicação, ou `null` quando NÃO há coleta válida:
 * a contagem (`acessosDiasMes`) e o carimbo (`acessosColetadoEm`) precisam
 * existir juntos — sem o carimbo não se sabe de que mês é o número, logo não
 * há denominador para o "NN/DD", e a célula mostra "—". É a mesma regra da
 * apresentação (`deckAcessos`), menos o `acessosErro`: aqui o erro vai para o
 * tooltip e o último número válido continua na tela, que é o que a auditoria
 * quer conferir.
 */
export function diasComAcesso(a: App): number | null {
  if (typeof a.acessosDiasMes !== 'number') return null;
  if (!a.acessosColetadoEm) return null;
  return a.acessosDiasMes;
}

/**
 * Dias ÚTEIS (seg–sex) do mês do calendário em que a coleta rodou — no fuso de
 * Recife (UTC-3, sem horário de verão), que é o "dia" que a coleta usa para
 * contar. Um ISO de 01/03 01:30Z é 28/02 22:30 em Recife: o mês certo é
 * fevereiro. Cópia pura de `diasDoMesDeColeta` da apresentação, para a tabela
 * do inventário mostrar a MESMA fração "12/22" que o slide mostra.
 */
export function diasUteisDoMes(iso: string): number {
  return diasUteisDoMesDe(iso);
}

/**
 * Texto da célula Dias úteis: "NN/DD" — dias úteis com acesso, em dois dígitos,
 * sobre os dias úteis do mês da coleta ("02/22", "00/22") — ou "—" quando não
 * há contagem OU não há carimbo de coleta.
 */
export function textoDias(dias: number | null, coletadoEm: string | null | undefined): string {
  if (dias === null || !coletadoEm) return '—';
  return `${String(dias).padStart(2, '0')}/${String(diasUteisDoMes(coletadoEm)).padStart(2, '0')}`;
}

/** Rótulo humano da visibilidade — o select e o filtro falam a língua do selo. */
export const VISIBILIDADE_ROTULO: Record<string, string> = {
  publico: 'Público',
  privado: 'Privado',
  interno: 'Interno',
};

/** Placeholder de custo ausente — nunca "US$ 0,00", que mentiria sobre o gasto. */
export const CUSTO_VAZIO = '—';

/** Texto EXIBIDO da coluna — é contra ele que o filtro digitado/selecionado casa. */
export function textoColuna(a: App, col: ColunaTabela, custoUsd: number | null = null): string {
  if (col === 'docs') return `${docsFilled(a)}/${DOC_KEYS.length}`;
  if (col === 'atualizada') return a.lastUpdate ? formatDateBR(a.lastUpdate) : '';
  if (col === 'visibilidade') return VISIBILIDADE_ROTULO[a.visibility] ?? a.visibility;
  if (col === 'custo') return custoUsd === null ? CUSTO_VAZIO : formatUsdBR(custoUsd);
  if (col === 'apresentacao')
    return a.apresentacaoDesabilitada ? APRESENTACAO_ROTULO.oculto : APRESENTACAO_ROTULO.visivel;
  if (col === 'dias') return textoDias(diasComAcesso(a), a.acessosColetadoEm);
  return String(valorColuna(a, col) ?? '');
}

/**
 * O filtro de UMA coluna casa com a aplicação? Texto livre e selects de valor
 * exibido casam por `includes` no texto da célula; Apresentação e Dias úteis
 * têm opções FIXAS (chave no estado, não o texto), então casam pela chave.
 */
export function casaFiltro(a: App, col: ColunaTabela, v: string): boolean {
  if (!v) return true;
  if (col === 'apresentacao') return (a.apresentacaoDesabilitada ? 'oculto' : 'visivel') === v;
  if (col === 'dias') return faixaDias(diasComAcesso(a)) === v;
  return textoColuna(a, col).toLowerCase().includes(v.trim().toLowerCase());
}
